import { Trans, useTranslation } from "react-i18next";
import { Gift, Ticket } from "lucide-react";
import confetti from "canvas-confetti";

import { cn, formatDate } from "@/lib/utils";
import type { CollectorsDiscountData } from "@/hooks/use-apply-payment";

// Inline version of CollectorsDiscountModal for the wash start guide's
// success view. Same data, two variants: still collecting / coupon won.
export function CollectorsDiscountCard({
  data,
  currencySymbol,
}: {
  data: CollectorsDiscountData;
  currencySymbol: string;
}) {
  return data.couponCode ? (
    <CouponWon data={data} currencySymbol={currencySymbol} />
  ) : (
    <CollectingProgress data={data} />
  );
}

function CollectingProgress({ data }: { data: CollectorsDiscountData }) {
  const collected = data.collectorsDiscountCount;
  const needed = data.neededTransactions;
  const remaining = Math.max(0, needed - collected);

  return (
    <div className="w-full rounded-3xl border-4 border-bub-vivid-blue/30 bg-white px-10 py-6 text-left">
      <div className="flex items-center gap-4">
        <Ticket className="size-12 shrink-0" stroke="#009dc9" />
        <p className="text-[34px] font-fancy font-bold leading-tight">
          {data.xHead || <Trans>Leárazás</Trans>}
        </p>
      </div>
      <p className="text-[24px] mt-2">
        {data.xBody || (
          <Trans>
            A tranzakcióddal részt vettél egy leárazásban. Indíts több
            tranzakciót és szerezz kuponokat!
          </Trans>
        )}
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        {Array.from({ length: needed }, (_, index) => (
          <span
            key={index}
            className={cn(
              "size-9 rounded-full border-4",
              index < collected
                ? "bg-bub-orange border-bub-orange"
                : "border-bub-dark-blue/20",
            )}
          />
        ))}
      </div>
      <p className="text-[24px] font-bold mt-3">
        <Trans values={{ collected, needed, remaining }}>
          {{ collected }} / {{ needed }} — még {{ remaining }} tranzakció a
          kuponig
        </Trans>
      </p>
    </div>
  );
}

function CouponWon({
  data,
  currencySymbol,
}: {
  data: CollectorsDiscountData;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const receivedTransactions = data.receivedTransactions;
  const expiry = data.couponExpiry && formatDate(data.couponExpiry);
  const unit = data.discountType === "fix" ? currencySymbol : "%";
  const machineTypes = (data.machineType ?? [])
    .map((type) =>
      type === "mosogep"
        ? t("Mosógép")
        : type === "szaritogep"
          ? t("Szárítógép")
          : t("Ózon"),
    )
    .join(", ");

  return (
    <div className="w-full rounded-3xl bg-bub-orange px-10 py-6 text-left text-white">
      <Confetti />
      <div className="flex items-center gap-4">
        <Gift className="size-12 shrink-0" stroke="white" />
        <p className="text-[34px] font-fancy font-bold leading-tight !text-white">
          {data.yHead || <Trans>Gratulálunk!</Trans>}
        </p>
      </div>
      <p className="text-[24px] mt-2 !text-white">
        {data.yBody || (
          <Trans>
            Sikeresen elérted a szükséges tranzakció számot, mely után az alábbi
            kedvezményes kupont kapod most tőlünk ajándékba!
          </Trans>
        )}
      </p>
      <div className="flex items-center gap-6 mt-4">
        <div className="rounded-2xl border-4 border-dashed border-white/70 bg-white/15 px-6 py-3 text-center">
          <p className="text-[52px] font-fancy font-bold leading-none !text-white">
            -{data.discount} {unit}
          </p>
          <p className="text-[24px] font-bold tracking-widest mt-2 !text-white">
            {data.couponCode}
          </p>
        </div>
        <div className="text-[22px] space-y-1">
          {machineTypes && (
            <p className="!text-white">
              <Trans values={{ machineTypes }}>
                Erre tudod felhasználni: {{ machineTypes }}
              </Trans>
            </p>
          )}
          {expiry && (
            <p className="!text-white">
              <Trans values={{ expiry, receivedTransactions }}>
                Felhasználható: {{ expiry }}-ig, {{ receivedTransactions }}{" "}
                alkalommal
              </Trans>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ["#f45e22", "#009dc9", "#005D86", "#00aa2b", "#ffc93c"];
// canvas-confetti on a full-screen canvas. Started from a ref callback (React
// 19 runs its cleanup on unmount), so no useEffect needed. 2D canvas only, so
// it works with the kiosk's GPU disabled.
function Confetti() {
  return (
    <canvas
      ref={startConfetti}
      className="fixed inset-0 size-full pointer-events-none z-50"
    />
  );
}

function startConfetti(canvas: HTMLCanvasElement | null) {
  if (!canvas) return;
  const fire = confetti.create(canvas, { resize: true });
  // one celebratory burst from each bottom corner, aimed up the sides so it
  // frames the content instead of covering the text
  const cannons = (particleCount: number) => {
    fire({ ...CANNON, particleCount, angle: 65, origin: { x: 0, y: 1 } });
    fire({ ...CANNON, particleCount, angle: 115, origin: { x: 1, y: 1 } });
  };
  cannons(45);
  const second = setTimeout(() => cannons(25), 350);

  return () => {
    clearTimeout(second);
    fire.reset();
  };
}

const CANNON = {
  colors: CONFETTI_COLORS,
  scalar: 1.5,
  spread: 40,
  startVelocity: 70,
  ticks: 180,
};
